import { useState, type FormEvent, type ReactNode } from 'react';
import { api, mensajeDe } from '../../services/api';
import { alTocar } from '../../components/ruta';
import { Aviso, Boton, Campo, ENTRADA, Tarjeta } from '../../components/ui';

// Las tres pantallas de "antes de estar adentro": la puesta en marcha (una sola
// vez en la vida de la app), entrar, y elegir un PIN propio.

function Marco({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <div className="mx-auto flex min-h-screen max-w-sm flex-col gap-4 px-4 py-8">
      <a href="/" onClick={alTocar('/')} className="text-sm font-bold text-(--color-acento)">‹ EnCancha</a>
      <header>
        <p className="text-xs font-bold uppercase tracking-widest text-(--color-acento)">Organizadores</p>
        <h1 className="font-titulo text-4xl uppercase">{titulo}</h1>
      </header>
      {children}
      <a href="/guia-organizador" target="_blank" rel="noopener" className="flex min-h-12 items-center justify-between gap-3 rounded-xl border border-(--color-borde) bg-(--color-panel) px-4 py-3 text-(--color-acento)">
        <span><strong className="block text-sm">Guía de uso</strong><span className="block text-xs text-(--color-apagado)">Aprendé a organizar tu torneo. No necesitás ingresar.</span></span>
        <span aria-hidden="true">↗</span>
      </a>
    </div>
  );
}

/** El PIN: 4 números, con el teclado numérico del celular. */
function CampoPin({ etiqueta, valor, onChange, autoFocus }: { etiqueta: string; valor: string; onChange: (v: string) => void; autoFocus?: boolean }) {
  return (
    <Campo etiqueta={etiqueta}>
      <input
        className={`${ENTRADA} tracking-[0.5em]`}
        type="password"
        inputMode="numeric"
        autoComplete="off"
        maxLength={4}
        value={valor}
        autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 4))}
      />
    </Campo>
  );
}

function useEnvio(accion: () => Promise<void>) {
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState('');
  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setError('');
    try {
      await accion();
    } catch (err) {
      setError(mensajeDe(err));
    } finally {
      setEnviando(false);
    }
  };
  return { enviando, error, enviar };
}

export function PrimeraVez({ alTerminar }: { alTerminar: () => void }) {
  const [clave, setClave] = useState('');
  const [organizacion, setOrganizacion] = useState('');
  const [nombre, setNombre] = useState('');
  const [pin, setPin] = useState('');
  const [pin2, setPin2] = useState('');

  const { enviando, error, enviar } = useEnvio(async () => {
    if (pin !== pin2) throw new Error('Los dos PIN no coinciden.');
    await api.setup({ clave_alta: clave, organizacion, nombre, pin });
    await api.entrar(nombre, pin);
    alTerminar();
  });

  return (
    <Marco titulo="Primera vez">
      <Aviso>
        Esto se hace una sola vez: crea el club y el primer organizador. Después, desde adentro, se suman los demás.
      </Aviso>
      <Tarjeta>
        <form onSubmit={enviar} className="flex flex-col gap-3">
          <Campo etiqueta="Clave de puesta en marcha" ayuda="Es la CLAVE_ALTA del servidor.">
            <input className={ENTRADA} type="password" value={clave} onChange={(e) => setClave(e.target.value)} />
          </Campo>
          <Campo etiqueta="Club u organización">
            <input className={ENTRADA} value={organizacion} onChange={(e) => setOrganizacion(e.target.value)} placeholder="Club La Esquina" />
          </Campo>
          <Campo etiqueta="Tu nombre" ayuda="Con este nombre vas a entrar.">
            <input className={ENTRADA} value={nombre} onChange={(e) => setNombre(e.target.value)} autoComplete="username" />
          </Campo>
          <CampoPin etiqueta="Elegí un PIN (4 números)" valor={pin} onChange={setPin} />
          <CampoPin etiqueta="Repetilo" valor={pin2} onChange={setPin2} />
          {error && <Aviso tono="error">{error}</Aviso>}
          <Boton type="submit" ancho deshabilitado={enviando || !clave || !organizacion || !nombre || pin.length !== 4}>
            {enviando ? 'Creando...' : 'Crear y entrar'}
          </Boton>
        </form>
      </Tarjeta>
    </Marco>
  );
}

export function Entrar({ alEntrar }: { alEntrar: () => void }) {
  const [nombre, setNombre] = useState('');
  const [pin, setPin] = useState('');
  const { enviando, error, enviar } = useEnvio(async () => {
    await api.entrar(nombre, pin);
    alEntrar();
  });

  return (
    <Marco titulo="Entrar">
      <Tarjeta>
        <form onSubmit={enviar} className="flex flex-col gap-3">
          <Campo etiqueta="Tu nombre">
            <input className={ENTRADA} value={nombre} onChange={(e) => setNombre(e.target.value)} autoComplete="username" autoFocus />
          </Campo>
          <CampoPin etiqueta="PIN" valor={pin} onChange={setPin} />
          {error && <Aviso tono="error">{error}</Aviso>}
          <Boton type="submit" ancho deshabilitado={enviando || !nombre || pin.length !== 4}>
            {enviando ? 'Entrando...' : 'Entrar'}
          </Boton>
        </form>
      </Tarjeta>
      <p className="text-center text-xs text-(--color-apagado)">
        ¿Te olvidaste el PIN? Pedile a otro organizador que te lo resetee.
      </p>
    </Marco>
  );
}

/** Elegir PIN propio. `obligado`: entró con uno provisorio y no puede hacer otra cosa. */
export function CambiarPin({ obligado, alTerminar }: { obligado?: boolean; alTerminar: () => void }) {
  const [actual, setActual] = useState('');
  const [nuevo, setNuevo] = useState('');
  const [nuevo2, setNuevo2] = useState('');
  const [listo, setListo] = useState(false);
  const { enviando, error, enviar } = useEnvio(async () => {
    if (nuevo !== nuevo2) throw new Error('Los dos PIN nuevos no coinciden.');
    await api.cambiarPin(actual, nuevo);
    setListo(true);
    setActual('');
    setNuevo('');
    setNuevo2('');
    alTerminar();
  });

  const formulario = (
    <form onSubmit={enviar} className="flex flex-col gap-3">
      <CampoPin etiqueta={obligado ? 'El PIN que te pasaron' : 'PIN actual'} valor={actual} onChange={setActual} />
      <CampoPin etiqueta="PIN nuevo" valor={nuevo} onChange={setNuevo} />
      <CampoPin etiqueta="Repetilo" valor={nuevo2} onChange={setNuevo2} />
      {error && <Aviso tono="error">{error}</Aviso>}
      {listo && !error && <Aviso tono="bien">Listo, PIN cambiado.</Aviso>}
      <Boton type="submit" ancho deshabilitado={enviando || actual.length !== 4 || nuevo.length !== 4}>
        {enviando ? 'Guardando...' : 'Guardar PIN'}
      </Boton>
    </form>
  );

  if (!obligado) return formulario;
  return (
    <Marco titulo="Tu PIN">
      <Aviso>Entraste con un PIN provisorio. Elegí uno propio para seguir: el provisorio deja de servir.</Aviso>
      <Tarjeta>{formulario}</Tarjeta>
    </Marco>
  );
}
